/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
/* eslint-disable camelcase */
import { PREAUTH_INFO, TS_HOST, AUTH_TOKEN, GET_AUTH_TOKEN, TIMEOUT_MS } from '../constants';

interface TokenResponse {
  data: {
    token: string;
  };
}

let cachedHost: string | null = null;
let token: string | null = null;
let infoObject: any = null;

export const getHostFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const host = urlParams.get('host') || null;
  return host;
};

export const getTokenFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get('token') || null;
  return urlToken;
};

export const getHost = (): string => {
  if (cachedHost) {
    return cachedHost;
  }

  const host = getHostFromUrl();
  if (host) {
    cachedHost = host;
  } else {
    cachedHost = TS_HOST;
  }
  return cachedHost;
};

const cleanup = (
  listener: ((event: MessageEvent) => void) | null,
  timeout: NodeJS.Timeout | null
) => {
  if (listener) {
    window.removeEventListener('message', listener);
  }
  if (timeout) {
    clearTimeout(timeout);
  }
};

export const createMessageHandler = (
  type: string,
  timeoutMs: number = TIMEOUT_MS,
  postMessageType: string = GET_AUTH_TOKEN
): Promise<TokenResponse> => {
  return new Promise<TokenResponse>((resolve, reject) => {
    let listener: ((event: MessageEvent) => void) | null = null;
    let timeout: NodeJS.Timeout | null = null;

    listener = (event: MessageEvent) => {
      const { data } = event;
      if (data?.type === type) {
        cleanup(listener, timeout);
        resolve(data);
      }
    };

    window.addEventListener('message', listener);

    timeout = setTimeout(() => {
      cleanup(listener, timeout);
      reject(new Error(`No message received within ${timeoutMs}ms for type: ${type}`));
    }, timeoutMs);

    try {
      window.parent?.parent?.postMessage({ type: postMessageType }, getHost());
    } catch (error) {
      cleanup(listener, timeout);
      reject(error);
    }
  });
};

export const getTokenService = async (): Promise<string> => {
  try {
    // Check if token is provided via URL parameter (for standalone/embed apps)
    const urlToken = getTokenFromUrl();
    if (urlToken) {
      token = urlToken;
      return token;
    }

    const response = await createMessageHandler(AUTH_TOKEN, TIMEOUT_MS, GET_AUTH_TOKEN);
    token = response.data.token;
    return token;
  } catch (error) {
    console.error('failed to fetch auth token', error);
    throw new Error('Failed to retrieve authentication token');
  }
};

/**
 * Gets cached token from URL, in-memory cache, SDK, or fetches a new one
 * Priority: URL param > In-memory > SDK cache > Fresh fetch
 */
export const getCachedToken = async (): Promise<string> => {
  if (token) {
    return token;
  }

  const urlToken = getTokenFromUrl();
  if (urlToken) {
    token = urlToken;
    return token;
  }

  const tsEmbedSDK = (window as any)._tsEmbedSDK;
  if (tsEmbedSDK && tsEmbedSDK.cachedAuthToken) {
    return tsEmbedSDK.cachedAuthToken;
  }

  return await getTokenService();
};

export const setInfoObject = (info: any) => {
  infoObject = info;
};

export const getInfoObject = async (): Promise<any> => {
  if (!infoObject) {
    await fetchPreauthInfoService();
  }
  return infoObject;
};

const getPreauthInfoPromise = async () => {
  const sessionInfoPath = `${getHost()}${PREAUTH_INFO}`;
  const token = await getCachedToken();
  const headers = {
    'x-requested-by': 'ThoughtSpot',
    'content-type': 'application/x-www-form-urlencoded',
    Authorization: `Bearer ${token}`,
  };

  return fetch(sessionInfoPath, {
    method: 'GET',
    headers,
    credentials: 'include',
  });
};

/**
 * Fetches the session info from the ThoughtSpot server.
 * @returns {Promise<any>}
 * @example
 * ```js
 *  const response = await sessionInfoService();
 * ```
 */
export async function fetchPreauthInfoService(): Promise<any> {
  let response: Response | null = null;
  try {
    response = await getPreauthInfoPromise();
    if (response.status === 200 && response.ok) {
      const data = await response.json();
      setInfoObject(data);
    } else {
      console.error(`Failed to retrieve preauth info: ${response.status}`);
      return null;
    }
  } catch (e) {
    setInfoObject({});
    console.error('failed to fetch preauth info', e);
    if (response && response.status === 401) {
      console.error(`Authorized access to preauth info: ${e}`);
    } else {
      console.error(`Failed to retrieve preauth info: ${e}`);
    }
    return null;
  }
}
